import { extractErrorMessage } from '@app/src/errors/extractErrorMessage'
import type { ErrorResponse } from '@app/src/types/api'
import type { UseFormMethods } from 'react-hook-form'

export interface RequestContext {
  method: string
  url: string
}

/**
 * Sentry titles an exception by its message, so an empty one collapses every
 * unhandled API rejection into a single untriageable "Error: No error message"
 * group. Derive a message from the response whenever the caller has none.
 */
const buildMessage = (statusCode: number, body: ErrorResponse | null, request?: RequestContext): string => {
  const target = request ? `${request.method} ${request.url}` : 'request'

  return `${statusCode} on ${target}: ${extractErrorMessage(statusCode, body)}`
}

class RequestError extends Error {
  statusCode: number
  body: ErrorResponse | null
  isValidationError: boolean
  request?: RequestContext

  constructor(statusCode: number, body: ErrorResponse | null, msg?: string, request?: RequestContext) {
    super(msg || buildMessage(statusCode, body, request))
    this.statusCode = statusCode
    this.body = body
    this.isValidationError = statusCode === 400
    this.request = request
    // Set the prototype explicitly. see https://github.com/Microsoft/TypeScript-wiki/blob/main/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
    Object.setPrototypeOf(this, RequestError.prototype)
  }

  /** Set by the backend when it has already reported the failure to Sentry itself. */
  get backendSentryId(): string | undefined {
    return this.body?.sentryId
  }

  get errorCode(): string | undefined {
    return this.body?.errorCode
  }

  setFormValidationErrors = (setError: UseFormMethods['setError']): boolean => {
    this.body?.validation?.items?.forEach((item): void => {
      setError(item.name, {
        type: 'manual',
        message: item.message,
      })
    })
    return this.isValidationError
  }
}

export default RequestError
